import { useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import {
  stations as mockStations,
  formatMoneyShort,
  OwnerStation,
  ownerStatusColor,
} from '@/mocks/ownerDashboard';

interface MapSectionProps {
  onStationClick: (station: OwnerStation) => void;
  onStationForecast?: (station: OwnerStation) => void;
  stations?: OwnerStation[];
  /** Hide money fields (dispatcher dashboard is technical-only). */
  hideFinance?: boolean;
}

const statusColor = (s: OwnerStation): string => ownerStatusColor(s.status);
const fmtGen = (v: number | null | undefined, t: (k: string) => string) =>
  v == null ? `— (${t('d_map_no_telemetry')})` : `${v.toFixed(1)} ${t('d_u_mwh')}`;

export default function MapSection({ onStationClick, onStationForecast, stations: liveStations, hideFinance }: MapSectionProps) {
  const stations = liveStations && liveStations.length ? liveStations : mockStations;
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [showWeather, setShowWeather] = useState(false);

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const mapBg = isDark ? '#0F0F0F' : '#E8EBF0';
  const chipBg = isDark ? 'rgba(255,255,255,0.06)' : '#F5F5F7';
  const chipBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';

  const tileUrl = isDark
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  const center: [number, number] = [47.5, 30.5];

  return (
    <div
      className="rounded-xl overflow-hidden flex flex-col h-full relative z-0"
      style={{ background: cardBg, border: `1px solid ${cardBorder}`, minHeight: 520 }}
    >
      <div
        className="px-4 py-3 flex items-center justify-between shrink-0"
        style={{ borderBottom: `1px solid ${cardBorder}` }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-line" style={{ color: '#D12229' }} />
          </div>
          {t('d_map_title')}
        </div>
        <button
          onClick={() => setShowWeather(!showWeather)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-all duration-150 whitespace-nowrap"
          style={{
            background: chipBg,
            border: `1px solid ${showWeather ? '#D12229' : chipBorder}`,
            color: showWeather ? '#D12229' : textMuted,
            fontFamily: 'Muller, Inter, sans-serif',
          }}
        >
          <span style={{ fontSize: 12 }}>☁️</span>
          {t('d_map_weather')}
        </button>
      </div>
      <div className="flex-1" style={{ minHeight: 460 }}>
        <MapContainer
          center={center}
          zoom={6}
          style={{ height: '100%', width: '100%', background: mapBg }}
          scrollWheelZoom={false}
        >
          <TileLayer url={tileUrl} attribution='&copy; OpenStreetMap &copy; CARTO' />
          {stations.map((s) => {
            const sc = statusColor(s);
            const radius = 8 + Math.min(14, s.installedMw * 1.5);
            return (
              <CircleMarker
                key={s.id}
                center={[s.lat, s.lon]}
                radius={radius}
                pathOptions={{ color: sc, fillColor: sc, fillOpacity: 0.65, weight: 2.5 }}
              >
                <LeafletTooltip direction="top" offset={[0, -radius - 6]}>
                  <div style={{ fontFamily: 'Muller, Inter, sans-serif', fontSize: 12, lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{s.name}</div>
                    <div style={{ color: sc, fontWeight: 600 }}>
                      ● {s.statusLabel}{s.statusNote ? ` (${s.statusNote})` : ''}
                    </div>
                    {showWeather && s.cloudPct != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, color: '#6B6B70', fontSize: 11 }}>
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={s.weatherIcon} />
                        </div>
                        {s.cloudPct}% хмар{s.insolaceWm2 != null ? ` · ${s.insolaceWm2} Вт/м²` : ''}
                      </div>
                    )}
                  </div>
                </LeafletTooltip>
                <Popup>
                  <div style={{ fontFamily: 'Muller, Inter, sans-serif', fontSize: 13, minWidth: 220, lineHeight: 1.7 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{s.name}</div>
                    <div>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: `${sc}20`,
                          color: sc,
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      >
                        ● {s.statusLabel}
                      </span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {s.statusNote && <div style={{ color: sc }}>{s.statusNote}</div>}
                      <div>{t('d_map_today_fact')}: {fmtGen(s.todayGenMwh, t)}</div>
                      {s.todayForecastMwh != null && <div>{t('d_map_forecast')}: {s.todayForecastMwh.toFixed(1)} {t('d_u_mwh')}</div>}
                      {!hideFinance && <div>{t('d_map_revenue_guar')}: {formatMoneyShort(s.todayRevenueUah)} {t('d_u_uah')}</div>}
                      {s.insolaceWm2 != null && <div>{t('d_map_insolation')}: {s.insolaceWm2} {t('d_u_wm2')}</div>}
                    </div>
                    {showWeather && s.cloudPct != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={s.weatherIcon} style={{ fontSize: 12 }} />
                        </div>
                        <span style={{ fontSize: 11, color: '#6B6B70' }}>
                          {s.cloudPct}% {t('d_map_cloud')}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); onStationForecast?.(s); }}
                        className="cursor-pointer whitespace-nowrap"
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          background: '#D12229',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {t('d_map_forecast')}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onStationClick(s); }}
                        className="cursor-pointer whitespace-nowrap"
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          background: 'transparent',
                          color: '#D12229',
                          border: '1px solid #D12229',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {t('d_btn_details')}
                      </button>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}